import { useId } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import type { LabelPlacement } from '../../stores/themeStore';

/**
 * Props every shared form field takes. The value callback of each component
 * (onValueChanged) fires ONLY on user interaction: typing, picking, clearing,
 * stepping. Changing the `value` prop never calls it, so seeding a field from
 * loaded data cannot echo back as an edit (the DevExtreme seeding bug).
 */
export interface BaseFieldProps {
  /** Visible label, wired to the input with htmlFor/id, so the accessible
   *  name is exactly this text. Never repeated as a placeholder. Where it
   *  goes is `labelPlacement`. */
  label?: string;
  /** Where the label goes: 'border' (on the outline), 'above' or 'left'
   *  (beside, wrapping to above on a phone). Default: the app-wide setting
   *  (User Settings > Field labels), which defaults to 'border'. Set it only
   *  where a layout needs one placement whatever the user picked. */
  labelPlacement?: LabelPlacement;
  /** Accessible name when the visible label lives outside the component.
   *  Ignored when `label` is set. */
  ariaLabel?: string;
  /** Hint shown inside the empty field. Only for extra information
   *  ("Search ingredients"), never the label again. */
  placeholder?: string;
  /** Adds a required marker to the label and aria-required to the input. */
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  /** Marks the field invalid (red outline, aria-invalid). */
  error?: boolean;
  /** AppSelectBox name for `error`, inverted: false marks the field invalid. */
  isValid?: boolean;
  /** Shown under the field in the error colour; implies `error`. */
  errorText?: string;
  /** Shown under the field, 4px below it. Text, or text with a link. */
  helperText?: ReactNode;
  /** The control's width; a number is px. Beside a label ('left') a px width
   *  is the box alone, the label column is added outside it. Default fills the container. */
  width?: number | string;
  /** Field height in px, for the few callers that pin one (AppSelectBox `height`).
   *  Default is the --input-height token, which follows the UI size setting. */
  height?: number;
  className?: string;
  /** Extra attributes for the <input>, merged last. An `id` here is used for
   *  the label's htmlFor. A string `style` ("text-transform: uppercase", as
   *  AppTextBox callers pass it) is converted to an object. */
  inputAttr?: Record<string, unknown>;
}

export function isInvalid(p: Pick<BaseFieldProps, 'error' | 'isValid' | 'errorText'>): boolean {
  return !!p.error || p.isValid === false || !!p.errorText;
}

/** "text-transform: uppercase; color: red" to { textTransform: 'uppercase', color: 'red' }. */
export function styleFromString(css: string): CSSProperties {
  const out: Record<string, string> = {};
  for (const part of css.split(';')) {
    const i = part.indexOf(':');
    if (i < 0) continue;
    const key = part.slice(0, i).trim().replace(/-([a-z])/g, (_m, c: string) => c.toUpperCase());
    const val = part.slice(i + 1).trim();
    if (key && val) out[key] = val;
  }
  return out as CSSProperties;
}

/** inputAttr as React props for an <input>: string style converted, id removed (used separately). */
export function inputAttrProps(inputAttr?: Record<string, unknown>): Record<string, unknown> {
  if (!inputAttr) return {};
  const { id: _id, style, ...rest } = inputAttr;
  void _id;
  return {
    ...rest,
    ...(typeof style === 'string' ? { style: styleFromString(style) } : style ? { style } : {}),
  };
}

/** Ids for the input and its helper and error lines. */
export function useFieldIds(inputAttr?: Record<string, unknown>) {
  const auto = useId();
  const id = typeof inputAttr?.id === 'string' ? inputAttr.id : `fld${auto.replace(/[^a-zA-Z0-9_-]/g, '')}`;
  return { id, helperId: `${id}-help`, errorId: `${id}-err` };
}

/** aria-describedby for the input: whichever of helper and error text is shown. */
export function describedBy(ids: { helperId: string; errorId: string }, p: Pick<BaseFieldProps, 'helperText' | 'errorText'>): string | undefined {
  const parts = [p.errorText ? ids.errorId : null, p.helperText ? ids.helperId : null].filter(Boolean);
  return parts.length ? parts.join(' ') : undefined;
}

export function widthStyle(width?: number | string): CSSProperties | undefined {
  return width == null ? undefined : { width, maxWidth: '100%' };
}

/** A width in px (a number or "120px"), as a CSS length; null for %, auto, calc() and the rest. */
function pxWidth(width?: number | string): string | null {
  if (typeof width === 'number') return Number.isFinite(width) ? `${width}px` : null;
  return typeof width === 'string' && /^\d+(\.\d+)?px$/.test(width.trim()) ? width.trim() : null;
}

/**
 * The frame's width for a field `width`. A px `width` is the CONTROL's width
 * in every placement: beside a label ('left') the frame adds the label column
 * and the gap outside it, so `width={120}` is a 120px box with its label to
 * the left, not 120px shared between them (which left a date box about 10px
 * wide). A relative width ('100%', 'auto', calc) sizes the whole field, label
 * included, as it always did. Above and on the border the frame and the
 * control are the same width.
 */
export function fieldWidthStyle(width: number | string | undefined, placement: LabelPlacement, labelled: boolean): CSSProperties | undefined {
  const px = pxWidth(width);
  if (placement !== 'left' || !labelled || px == null) return widthStyle(width);
  return {
    width: `calc(var(--field-label-width, 140px) + var(--space-3, 12px) + ${px})`,
    maxWidth: '100%',
    '--app-field-input-width': px,
  } as CSSProperties;
}

/** Whether fieldWidthStyle sized the control column (adds the app-field--sized class). */
export function sizesControl(width: number | string | undefined, placement: LabelPlacement, labelled: boolean): boolean {
  return placement === 'left' && labelled && pxWidth(width) != null;
}

/**
 * The input's aria-label. Without a visible label: `ariaLabel`. With one, the
 * <label htmlFor> names the input, except when the field is required: the
 * label then also holds the "*" marker, and although it is aria-hidden (so a
 * screen reader hears "Last Name"), label-text lookups (Testing Library's
 * getByLabelText, Playwright's getByLabel with exact) read "Last Name*". The
 * Settings wrappers always put the label text on the input as aria-label, so
 * e2e specs and tests found required fields by their plain label; this keeps
 * that true now that the label is the field's own. Same text as the visible
 * label, so the name still matches what is on screen.
 */
export function inputAriaLabel(label: string | undefined, required: boolean | undefined, ariaLabel: string | undefined): string | undefined {
  if (!label) return ariaLabel;
  return required ? label : undefined;
}
